import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useDashboardStore } from '../stores/useDashboardStore';
import { useRBAC } from '../hooks/useRBAC'; // Use centralized RBAC hook
import { useUserStore } from '../stores/useUserStore';
import { InventoryQRScannerDialog } from '../inventory/components/InventoryQRScannerDialog';
import { InventoryStockUpdateDialog } from '../inventory/components/InventoryStockUpdateDialog';
import { InventoryService } from '../services/inventoryService';
import { Inventory } from 'public-contracts';
import { attendanceService } from '../services/attendanceService';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

const ModernDashboard: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { stats, isLoading, error, isStaffView, fetchDashboardData } =
    useDashboardStore();
  const {
    canManageInventory,
    canAccessResource,
    hasPermission,
    highestRole,
    isAdminLevel,
    isStaffLevel,
  } = useRBAC(); // Use centralized RBAC hook
  const { user } = useUserStore();
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [stockUpdateDialogOpen, setStockUpdateDialogOpen] = useState(false);
  const [scannedInventory, setScannedInventory] = useState<Inventory | null>(
    null
  );
  const [checkinStatus, setCheckinStatus] = useState<{
    checkedIn: boolean;
    onBreak: boolean;
    since?: Date;
  } | null>(null);

  // Staff can scan QR codes even if they can't manage inventory
  const canScanInventory = canAccessResource('inventory');

  const handleQRScanSuccess = useCallback(
    async (inventoryData: any) => {
      console.log('QR scan successful:', inventoryData);
      setQrScannerOpen(false);

      try {
        // Fetch the full inventory data
        const scannedInv = await InventoryService.getInventory(
          inventoryData.inventoryId
        );
        setScannedInventory(scannedInv);
        setStockUpdateDialogOpen(true);
      } catch (error) {
        console.error('Failed to load inventory:', error);
        // If staff can't access inventory page, just show error
        if (!canManageInventory) {
          alert('Failed to load inventory data. Please try again.');
        } else {
          navigate('/inventory');
        }
      }
    },
    [navigate, canManageInventory]
  );

  const handleQRScanError = useCallback((error?: string) => {
    console.error('QR scan error:', error);
    setQrScannerOpen(false);
  }, []);

  // Helper function to translate time strings
  const translateTime = (timeString: string): string => {
    if (timeString.includes('|')) {
      const [value, key] = timeString.split('|');
      return `${value} ${translate(key)}`;
    }
    if (timeString.startsWith('pos.dashboard.')) {
      return translate(timeString);
    }
    return timeString;
  };

  useEffect(() => {
    fetchDashboardData(highestRole || undefined);
  }, [fetchDashboardData, highestRole]);

  // Load attendance/check-in status for quick action
  useEffect(() => {
    const loadStatus = async () => {
      try {
        if (!user?.id) return;
        // Today's attendance records
        const todayRecords = await attendanceService.getTodayAttendance(
          String(user.id)
        );
        let checkedIn = false;
        let since: Date | undefined;
        if (todayRecords.length > 0) {
          const last = [...todayRecords].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          )[todayRecords.length - 1];
          checkedIn = !!last.isIn;
          since = new Date(last.date);
        }
        // Break status
        let onBreak = false;
        try {
          const br = await attendanceService.getBreakStatus(String(user.id));
          onBreak = br.isOnBreak;
          if (br.isOnBreak && br.breakStartTime) {
            since = new Date(br.breakStartTime);
          }
        } catch {
          // Ignore break status errors in dashboard summary
        }
        setCheckinStatus({ checkedIn, onBreak, since });
      } catch {
        setCheckinStatus(null);
      }
    };
    loadStatus();
  }, [user?.id]);

  const formatTime = (d?: Date) => {
    if (!d) return '';
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if user has inventory management permissions
  const hasInventoryPermission = () => {
    return canManageInventory;
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-center">
            <LoadingSpinner size="lg" variant="primary" />
            <span className="ml-2 text-muted-foreground">
              {translate('pos.common.action.loading')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="text-center text-destructive">
            <p>
              {translate('pos.common.action.error_loading')}: {error}
            </p>
            <button
              onClick={() => fetchDashboardData(highestRole || undefined)}
              className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              {translate('pos.common.action.refresh')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const quickActionsCard = (
    <div className="bg-card p-6 rounded-lg shadow-sm border">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        ⚡ {translate('pos.common.action.actions')}
      </h3>
      <div className="space-y-2">
        {/* Create Order - Available for all roles */}
        <button
          onClick={() => navigate('/orders/create')}
          className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
        >
          🛒 {translate('pos.orders.create')}
        </button>

        {/* Customers - Only for admin/orgAdmin (not storeManager or staff) */}
        {canAccessResource('customers') && isAdminLevel && (
          <button
            onClick={() => navigate('/customers')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            👥 {translate('pos.customers.name', { smart_count: 2 })}
          </button>
        )}

        {/* Products - Only for admin/orgAdmin (not storeManager or staff) */}
        {canAccessResource('products') && isAdminLevel && (
          <button
            onClick={() => navigate('/products')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            ☕ {translate('pos.products.name', { smart_count: 2 })}
          </button>
        )}

        {/* Inventory - Only for admin/orgAdmin (not storeManager or staff) */}
        {hasInventoryPermission() && isAdminLevel && (
          <button
            onClick={() => navigate('/inventory')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            📦 {translate('pos.inventory.name')}
          </button>
        )}

        {/* Scan QR - Available for staff and above */}
        {canScanInventory && (
          <button
            onClick={() => setQrScannerOpen(true)}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            📱 {translate('pos.inventory.scanQR')}
          </button>
        )}

        {/* Orders Report - Available for all roles with access */}
        {canAccessResource('orders_report') && (
          <button
            onClick={() => navigate('/report/orders')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            📊 {translate('pos.orders_report.name')}
          </button>
        )}

        {/* Check-in/Check-out - Available for roles with staff_profiles access */}
        {canAccessResource('staff_profiles') && (
          <button
            onClick={() => navigate('/staff_profiles?tab=checkin')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            {checkinStatus
              ? checkinStatus.onBreak
                ? '☕'
                : checkinStatus.checkedIn
                ? '🟢'
                : '🔴'
              : '⏱️'}{' '}
            {checkinStatus
              ? checkinStatus.onBreak
                ? translate('pos.staff.attendance.status_on_break', 'On Break')
                : checkinStatus.checkedIn
                ? translate('pos.staff.attendance.checked_in', 'Checked In')
                : translate('pos.staff.attendance.checked_out', 'Checked Out')
              : translate('pos.staff.tabs.checkin_checkout', 'Check-in/Check-out')}
          </button>
        )}

        {/* Costs - Staff can create, admins can manage */}
        {hasPermission('costs', 'create') && highestRole === 'staff' && (
          <button
            onClick={() => navigate('/costs/create')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            💰 {translate('pos.costs.create_cost', 'Add Cost')}
          </button>
        )}
        {canAccessResource('costs') && isAdminLevel && (
          <button
            onClick={() => navigate('/costs')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            💰 {translate('pos.costs.name')}
          </button>
        )}

        {/* Settings - Only for admin/orgAdmin (not storeManager or staff) */}
        {canAccessResource('settings') && isAdminLevel && (
          <button
            onClick={() => navigate('/settings')}
            className="w-full p-3 text-left border rounded-lg hover:bg-muted transition-colors"
          >
            ⚙️ {translate('pos.settings.name')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Dashboard Header */}
      <div className="bg-card p-6 rounded-lg shadow-sm border">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {translate('pos.dashboard.name')}
        </h1>
        <p className="text-muted-foreground">
          {translate('pos.dashboard.welcome.subtitle')}
        </p>
      </div>

      {/* Quick Actions (mobile first) */}
      <div className="block md:hidden">{quickActionsCard}</div>

      {/* Key Metrics Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {isStaffView
                  ? translate(
                      'pos.dashboard.revenue_last_6_hours',
                      'Doanh thu 6 giờ qua'
                    )
                  : translate('pos.dashboard.monthly_revenue')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                <span data-sensitive-field="amount">
                  {stats.todayRevenue.toLocaleString()}₫
                </span>
              </p>
            </div>
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <span className="text-green-600 dark:text-green-400">💰</span>
            </div>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
            {stats.revenueGrowth > 0 ? '+' : ''}
            {stats.revenueGrowth.toFixed(1)}%{' '}
            {isStaffView
              ? translate(
                  'pos.dashboard.vs_previous_6_hours',
                  'so với 6 giờ trước'
                )
              : translate('pos.dashboard.vs_yesterday')}
          </p>
        </div>

        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {isStaffView
                  ? translate(
                      'pos.dashboard.orders_last_6_hours',
                      'Đơn hàng 6 giờ qua'
                    )
                  : translate('pos.dashboard.new_orders')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {stats.todayOrders}
              </p>
            </div>
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400">🛒</span>
            </div>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
            {stats.ordersGrowth > 0 ? '+' : ''}
            {stats.ordersGrowth.toFixed(1)}%{' '}
            {isStaffView
              ? translate(
                  'pos.dashboard.vs_previous_6_hours',
                  'so với 6 giờ trước'
                )
              : translate('pos.dashboard.vs_yesterday')}
          </p>
        </div>

        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {isStaffView
                  ? translate(
                      'pos.dashboard.customers_last_6_hours',
                      'Khách hàng 6 giờ qua'
                    )
                  : translate('pos.dashboard.new_customers')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {stats.todayCustomers}
              </p>
            </div>
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <span className="text-purple-600 dark:text-purple-400">👥</span>
            </div>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
            {stats.customersGrowth > 0 ? '+' : ''}
            {stats.customersGrowth.toFixed(1)}%{' '}
            {isStaffView
              ? translate(
                  'pos.dashboard.vs_previous_6_hours',
                  'so với 6 giờ trước'
                )
              : translate('pos.dashboard.vs_yesterday')}
          </p>
        </div>

        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {isStaffView
                  ? translate(
                      'pos.dashboard.pending_orders_last_6_hours',
                      'Đơn hàng chờ duyệt (6 giờ qua)'
                    )
                  : translate('pos.dashboard.pending_orders')}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {stats.pendingOrders}
              </p>
            </div>
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
              <span className="text-orange-600 dark:text-orange-400">⏰</span>
            </div>
          </div>
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
            {translate('pos.dashboard.need_attention')}
          </p>
        </div>
      </div>

      {/* Sales Chart and Top Products */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        {/* Sales Chart */}
        <div className="col-span-4 bg-card p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {isStaffView
              ? translate(
                  'pos.dashboard.revenue_last_6_hours_chart',
                  'Doanh thu theo giờ (6 giờ qua)'
                )
              : translate('pos.dashboard.month_history')}
          </h3>
          <div className="space-y-4">
            {stats.hourlySales.length > 0 ? (
              stats.hourlySales.map((hourData) => {
                const maxRevenue = Math.max(
                  ...stats.hourlySales.map((h) => h.revenue)
                );
                const width =
                  maxRevenue > 0 ? (hourData.revenue / maxRevenue) * 100 : 0;
                return (
                  <div
                    key={hourData.hour}
                    className="flex items-center space-x-4"
                  >
                    <div className="w-16 text-sm text-muted-foreground">
                      {hourData.hour}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <div
                          className="h-2 bg-primary rounded"
                          style={{ width: `${Math.max(width, 2)}%` }}
                        />
                        <span className="text-sm font-medium text-foreground">
                          <span data-sensitive-field="amount">
                            {hourData.revenue.toLocaleString()}₫
                          </span>
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {hourData.orders}{' '}
                        {translate('pos.orders.name', {
                          smart_count: hourData.orders,
                        })}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <p>{translate('pos.dashboard.no_data_available')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="col-span-3 bg-card p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {isStaffView
              ? translate(
                  'pos.dashboard.top_products_last_6_hours',
                  'Sản phẩm hàng đầu (6 giờ qua)'
                )
              : translate('pos.campaign_report.top_products')}
          </h3>
          <div className="space-y-4">
            {stats.topSellingProducts.length > 0 ? (
              stats.topSellingProducts.map((product, index) => (
                <div key={product.name} className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {product.name.startsWith('pos.dashboard.')
                        ? translate(product.name)
                        : product.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {translate('pos.dashboard.top_products.sold', 'Sold')}{' '}
                      {product.quantity} •{' '}
                      <span data-sensitive-field="amount">
                        {product.revenue.toLocaleString()}₫
                      </span>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <p>{translate('pos.dashboard.no_data_available')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-card p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {isStaffLevel
            ? translate('pos.dashboard.orders_needing_attention')
            : translate('pos.orders.name', { smart_count: 2 })}
        </h3>
        <div className="space-y-4">
          {stats.recentOrders.length > 0 ? (
            stats.recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 dark:text-blue-400">☕</span>
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p
                      className="font-medium text-foreground truncate"
                      title={order.id}
                    >
                      {order.id}
                    </p>
                    <p
                      className="text-sm text-muted-foreground truncate"
                      title={
                        order.customer.startsWith('pos.dashboard.')
                          ? translate(order.customer)
                          : order.customer
                      }
                    >
                      {order.customer.startsWith('pos.dashboard.')
                        ? translate(order.customer)
                        : order.customer}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="font-medium text-foreground">
                      <span data-sensitive-field="amount">
                        {order.amount.toLocaleString()}₫
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {translateTime(order.time)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === 'delivered'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : order.status === 'ordered'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        : order.status === 'ready'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : order.status === 'cancelled'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {(() => {
                      const translationKey = `pos.orders.tabs.${order.status}`;
                      const translated = translate(translationKey);
                      // Fallback if translation fails
                      if (
                        translated === translationKey ||
                        translated.startsWith('pos.')
                      ) {
                        const fallbackTranslations: Record<string, string> = {
                          ordered: 'Đã đặt',
                          ready: 'Sẵn sàng',
                          delivered: 'Đã giao',
                          cancelled: 'Đã hủy',
                          // Legacy statuses for backward compatibility
                          completed: 'Đã giao',
                          pending: 'Đã đặt',
                          preparing: 'Sẵn sàng',
                        };
                        return (
                          fallbackTranslations[order.status] || order.status
                        );
                      }
                      return translated;
                    })()}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <p>{translate('pos.common.navigation.no_results')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="hidden md:block">{quickActionsCard}</div>

        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            🎯{' '}
            {isStaffView
              ? translate(
                  'pos.dashboard.goals_last_6_hours',
                  'Mục tiêu 6 giờ qua'
                )
              : translate('pos.dashboard.todays_goals')}
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1 text-foreground">
                <span>{translate('pos.dashboard.monthly_revenue')}</span>
                <span>{stats.revenueGoalProgress.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full"
                  style={{ width: `${stats.revenueGoalProgress}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1 text-foreground">
                <span>{translate('pos.orders.name', { smart_count: 2 })}</span>
                <span>{stats.ordersGoalProgress.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-green-500 dark:bg-green-600 h-2 rounded-full"
                  style={{ width: `${stats.ordersGoalProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            📊 {translate('pos.dashboard.performance')}
          </h3>
          <div className="space-y-4">
            {/* Campaign Performance Metrics (shown when active campaign exists) */}
            {stats.campaignAOV !== undefined && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {translate(
                      'pos.campaign_report.average_order_value',
                      'Average Order Value'
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      {new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                        minimumFractionDigits: 0,
                      }).format(stats.campaignAOV)}
                    </span>
                    {stats.campaignAOVTarget !== undefined && (
                      <span
                        className={`text-xs ${
                          stats.campaignAOV >= stats.campaignAOVTarget
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-muted-foreground'
                        }`}
                      >
                        /{' '}
                        {new Intl.NumberFormat('vi-VN', {
                          style: 'currency',
                          currency: 'VND',
                          minimumFractionDigits: 0,
                        }).format(stats.campaignAOVTarget)}
                      </span>
                    )}
                  </div>
                </div>
                {stats.campaignRepeatPurchaseRate !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {translate(
                        'pos.campaign_report.repeat_purchase_rate',
                        'Repeat Purchase Rate'
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {(stats.campaignRepeatPurchaseRate * 100).toFixed(1)}%
                      </span>
                      {stats.campaignRepeatPurchaseTarget !== undefined && (
                        <span
                          className={`text-xs ${
                            stats.campaignRepeatPurchaseRate >=
                            stats.campaignRepeatPurchaseTarget
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-muted-foreground'
                          }`}
                        >
                          /{' '}
                          {(stats.campaignRepeatPurchaseTarget * 100).toFixed(
                            1
                          )}
                          %
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {stats.campaignNewCustomerRate !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {translate(
                        'pos.campaign_report.new_customers',
                        'New Customers'
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {(stats.campaignNewCustomerRate * 100).toFixed(1)}%
                      </span>
                      {stats.campaignNewCustomerTarget !== undefined && (
                        <span
                          className={`text-xs ${
                            stats.campaignNewCustomerRate >=
                            stats.campaignNewCustomerTarget
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-muted-foreground'
                          }`}
                        >
                          / {(stats.campaignNewCustomerTarget * 100).toFixed(1)}
                          %
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className="border-t pt-4 mt-4">
                  <div className="text-xs text-muted-foreground mb-2">
                    {translate('pos.dashboard.other_metrics', 'Other Metrics')}
                  </div>
                </div>
              </>
            )}
            {/* Standard Performance Metrics */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {translate('pos.dashboard.avg_processing_time')}
              </span>
              <span className="text-sm font-medium text-foreground">
                {stats.avgProcessingTime.toFixed(1)} min
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {translate('pos.dashboard.satisfaction_rate')}
              </span>
              <span className="text-sm font-medium text-foreground">
                {stats.satisfactionRate.toFixed(1)}/5
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {translate('pos.dashboard.orders_per_hour')}
              </span>
              <span className="text-sm font-medium text-foreground">
                {stats.ordersPerHour.toFixed(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* QR Scanner Dialog */}
      <InventoryQRScannerDialog
        open={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onSuccess={handleQRScanSuccess}
        onError={handleQRScanError}
      />

      {/* Stock Update Dialog */}
      <InventoryStockUpdateDialog
        open={stockUpdateDialogOpen}
        onClose={() => {
          setStockUpdateDialogOpen(false);
          setScannedInventory(null);
        }}
        inventory={scannedInventory}
        onSuccess={() => {
          setStockUpdateDialogOpen(false);
          setScannedInventory(null);
          // Refresh dashboard data if needed
          fetchDashboardData();
        }}
      />
    </div>
  );
};

export default ModernDashboard;
